import React, { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useDispatch } from "react-redux";
import AddPaymentModal from "./AddPaymentModal";
import DeletePaymentMethodModal from "./DeletePaymentMethodModal";
import { ReactComponent as Delete } from "../../../assets/icons/delete-fill.svg";
import { getCardBrandMeta } from "../constants/cardBrand";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { clearBillingMessages } from "../store/billingSlice";

const CardIcon = ({ brand }) => {
  const meta = getCardBrandMeta(brand);
  return <img src={meta.icon} alt={meta.label} className="h-6" loading="eager" />;
};

function PaymentCard({ card, onDelete, onSetDefault, canEdit, isSettingDefault }) {
  return (
    <div className="flex items-center justify-between bg-white border border-line rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-4">
        <CardIcon brand={card.brand} />
        <div>
          <div className="text-sm text-[#171725]">
            •••• •••• •••• {card.last4}{" "}
            {card.default && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">
                Default
              </span>
            )}
          </div>
          <div className="text-xs text-subtle">
            {card.cardStatus === "expired" ? "Expired" : `Expires ${card.exp}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {canEdit && !card.default && (
          <button
            disabled={isSettingDefault}
            onClick={() => onSetDefault(card)}
            className="text-xs sm:text-sm text-primary border border-primary rounded-md px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSettingDefault ? "Setting..." : "Set as default"}
          </button>
        )}
        <button
          disabled={!canEdit}
          onClick={() => {
            if (canEdit) {
              onDelete(card);
            }
          }}
          className="disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Delete className="w-5 sm:w-7 h-6 sm:h-8" />
        </button>
      </div>
    </div>
  );
}

// Cards are saved by Stripe — either through the hosted payment link when an
// invoice is paid, or via the Add Payment Method modal (Stripe Elements
// tokenizes the card in the browser; only the pm_ token reaches our API).
export default function PaymentMethods() {
  const dispatch = useDispatch();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  const {
    cards,
    isLoading,
    isAttaching,
    isDeleting,
    settingDefaultId,
    apiError,
    canEditPaymentMethods,
    handleAttach,
    handleDelete,
    handleSetDefault,
  } = usePaymentMethods();

  useEffect(() => {
    if (apiError) {
      setErrorToastOpen(true);
    }
  }, [apiError]);

  const handleErrorToastClose = () => {
    setErrorToastOpen(false);
    dispatch(clearBillingMessages());
  };

  const handleDeleteClick = (card) => {
    setDeleteTarget(card);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    const didDelete = await handleDelete(deleteTarget.id);
    if (didDelete) {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl shadow-sm max-sm:px-4 p-6 border border-line sm:mx-2">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4 sm:gap-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Payment Methods</h2>
          <p className="text-sm text-mutedLight">
            Your cards are stored securely by Stripe
          </p>
        </div>
        <div>
          <button
            disabled={!canEditPaymentMethods}
            onClick={() => setAddOpen(true)}
            className="bg-slate-800 text-sm sm:text-base text-white px-4 py-1.5 sm:py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Payment Method
          </button>
        </div>
      </div>

      <Snackbar
        open={errorToastOpen}
        autoHideDuration={5000}
        onClose={handleErrorToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleErrorToastClose} severity="error" sx={{ width: "100%" }}>
          {apiError}
        </Alert>
      </Snackbar>

      {isLoading ? (
        <p className="text-sm text-mutedLight">Loading payment methods...</p>
      ) : (
        <div className="space-y-4">
          {cards.length === 0 ? (
            <p className="text-sm text-mutedLight">
              No payment methods saved yet. Add a card or pay an invoice through
              Stripe and it will appear here.
            </p>
          ) : (
            cards.map((card) => (
              <PaymentCard
                key={card.id}
                card={card}
                onDelete={handleDeleteClick}
                onSetDefault={(target) => handleSetDefault(target.id)}
                canEdit={canEditPaymentMethods}
                isSettingDefault={settingDefaultId === card.id}
              />
            ))
          )}
        </div>
      )}

      {canEditPaymentMethods ? (
        <>
          <AddPaymentModal
            open={addOpen}
            onClose={() => {
              if (!isAttaching) {
                setAddOpen(false);
              }
            }}
            onAdd={handleAttach}
            isSubmitting={isAttaching}
          />

          <DeletePaymentMethodModal
            open={Boolean(deleteTarget)}
            paymentMethod={deleteTarget}
            onClose={() => {
              if (!isDeleting) {
                setDeleteTarget(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            isSubmitting={isDeleting}
          />
        </>
      ) : null}
    </div>
  );
}
